import React from 'react';
import { useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconsSend from '@mui/icons-material/Send';
import Switch from '@mui/material/Switch';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { useProductQuery } from '../../../../../graphql/query/product/useProductQuery';
import useNotification from '../../../../components/_hook/useNotification';
import SelectSearchBrand from '../../../../components/_select/SelectSearchBrand';
import SelectSearchClassification from '../../../../components/_select/SelectSearchClassification';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';
import useForm from '../../../../components/_hook/useForm';
import SelectSearchTag from '../../../../components/_select/SelectSearchTag';
import NotFound from '../../../../components/_error/NotFound';
import { useBatchUpdateKeywordProductMutation } from '../../../../../graphql/mutation/product/batchUpdateKeywordProductMutation';
import Select from '../../../../components/_select/_module/Select';

interface IProps {
  productIds: string[];
  onSubmit?: () => void;
  onCancel: () => void;
}

export default function FormProductBatchUpdateKeyword(props: IProps) {
  const { t } = useTranslation();
  const { productIds, onSubmit, onCancel } = props;
  const { notification } = useNotification();
  const { data, loading } = useProductQuery(
    `
			_id
			keywords {
			  classifications
			  classificationsData {
			    _id
			    name
			  }
			  brands
			  brandsData {
			     _id
			     name
			  }
			  tags
			  tagsData {
			    _id
			    name
			  }
			}
		`,
    { variables: { filter: { _ids: productIds } } }
  );
  const [
    batchUpdateKeywordProductMutation,
  ] = useBatchUpdateKeywordProductMutation('isSuccess', {
    onCompleted: () => {
      notification.success(t('done'));
      if (onSubmit) {
        onSubmit();
      }
    },
    onError: (e) => {
      notification.error(e.message);
    },
  });
  const { values, setValue, handleSubmit, errors, isSubmitting } = useForm<{
    addKeywordClassifications: { label: string; value: string }[];
    addKeywordBrands: { label: string; value: string }[];
    addKeywordTags: { label: string; value: string }[];
    removeKeywordClassifications: { label: string; value: string }[];
    removeKeywordBrands: { label: string; value: string }[];
    removeKeywordTags: { label: string; value: string }[];
    removeAllKeyword: boolean;
  }>({
    defaultValues: {
      addKeywordClassifications: [],
      addKeywordBrands: [],
      addKeywordTags: [],
      removeKeywordClassifications: [],
      removeKeywordBrands: [],
      removeKeywordTags: [],
      removeAllKeyword: false,
    },
    onSubmit: async (tempValues) => {
      await batchUpdateKeywordProductMutation({
        variables: {
          updatingIds: productIds,
          addKeywordClassifications: tempValues.addKeywordClassifications.map(
            (addKeywordClassification) => addKeywordClassification.label
          ),
          addKeywordBrands: tempValues.addKeywordBrands.map(
            (addKeywordBrand) => addKeywordBrand.label
          ),
          addKeywordTags: tempValues.addKeywordTags.map(
            (addKeywordTag) => addKeywordTag.label
          ),
          removeKeywordClassifications: tempValues.removeKeywordClassifications.map(
            (removeKeywordClassification) => removeKeywordClassification.label
          ),
          removeKeywordBrands: tempValues.removeKeywordBrands.map(
            (removeKeywordBrand) => removeKeywordBrand.label
          ),
          removeKeywordTags: tempValues.removeKeywordTags.map(
            (removeKeywordTag) => removeKeywordTag.label
          ),
          removeAllKeyword: tempValues.removeAllKeyword,
        },
      });
    },
  });

  if (loading) {
    return (
      <Grid container justifyContent="center">
        <CircularProgress size={30} color="inherit" />
      </Grid>
    );
  }

  if (!data || data.products.items.length === 0) {
    return <NotFound />;
  }

  const products = data.products.items;
  const removeBrandOptions = Array.from(
    new Set(
      products
        .map((product) => {
          if (product.keywords && product.keywords.brandsData) {
            return product.keywords.brandsData;
          }
          return [];
        })
        .flat()
        .map((keywordData) => (keywordData ? keywordData.name : ''))
        .filter(Boolean)
    )
  ).map((name) => ({
    label: name,
    value: name,
  }));
  const removeTagOptions = Array.from(
    new Set(
      products
        .map((product) => {
          if (product.keywords && product.keywords.tagsData) {
            return product.keywords.tagsData;
          }
          return [];
        })
        .flat()
        .map((keywordData) => (keywordData ? keywordData.name : ''))
        .filter(Boolean)
    )
  ).map((name) => ({
    label: name,
    value: name,
  }));
  const removeClassificationOptions = Array.from(
    new Set(
      products
        .map((product) => {
          if (product.keywords && product.keywords.classificationsData) {
            return product.keywords.classificationsData;
          }
          return [];
        })
        .flat()
        .map((keywordData) => (keywordData ? keywordData.name : ''))
        .filter(Boolean)
    )
  ).map((name) => ({
    label: name,
    value: name,
  }));

  return (
    <>
      <DialogTitle style={{ textTransform: 'capitalize' }}>
        {t('batch')} {t('update')} {products.length}{' '}
        {t('category', { count: products.length })} {t('keyword')}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('__action____type__', {
                action: t('add'),
                type: t('keyword', { count: 2 }),
              })}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <SelectSearchClassification
              isMulti
              fullWidth
              menuPosition="fixed"
              onChange={(value) => setValue('addKeywordClassifications', value)}
              label={t('classification', { count: 2 })}
              value={values.addKeywordClassifications}
              error={Boolean(errors.addKeywordClassifications)}
              helperText={errors.addKeywordClassifications}
              placeholder={t('__action__relevant__type__', {
                action: t('insert'),
                type: t('classification', { count: 2 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectSearchBrand
              isMulti
              fullWidth
              menuPosition="fixed"
              onChange={(value) => setValue('addKeywordBrands', value)}
              label={t('brand', { count: 2 })}
              value={values.addKeywordBrands}
              error={Boolean(errors.addKeywordBrands)}
              helperText={errors.addKeywordBrands}
              placeholder={t('__action__relevant__type__', {
                action: t('insert'),
                type: t('brand', { count: 2 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <SelectSearchTag
              fullWidth
              isMulti
              menuPosition="fixed"
              onChange={(value) => setValue('addKeywordTags', value)}
              label={t('tag', { count: 2 })}
              value={values.addKeywordTags}
              error={Boolean(errors.addKeywordTags)}
              helperText={errors.addKeywordTags}
              placeholder={t('__action__relevant__type__', {
                action: t('insert'),
                type: t('tag', { count: 2 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle1">
              {t('__action____type__', {
                action: t('remove'),
                type: t('keyword', { count: 2 }),
              })}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Switch
                    checked={values.removeAllKeyword}
                    onChange={() => {
                      if (!values.removeAllKeyword) {
                        setValue('removeKeywordBrands', []);
                        setValue('removeKeywordClassifications', []);
                        setValue('removeKeywordTags', []);
                      }
                      setValue('removeAllKeyword', !values.removeAllKeyword);
                    }}
                    color="primary"
                  />
                }
                label={t('__action__all', { action: t('remove') })}
              />
            </FormGroup>
          </Grid>
          <Grid item xs={12}>
            <Select
              options={removeClassificationOptions}
              isMulti
              disabled={values.removeAllKeyword}
              fullWidth
              menuPosition="fixed"
              onChange={(value) =>
                setValue('removeKeywordClassifications', value)
              }
              label={t('classification', { count: 2 })}
              value={values.removeKeywordClassifications}
              error={Boolean(errors.removeKeywordClassifications)}
              helperText={errors.removeKeywordClassifications}
              placeholder={t('__action__relevant__type__', {
                action: t('select'),
                type: t('classification', { count: 2 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <Select
              options={removeBrandOptions}
              isMulti
              disabled={values.removeAllKeyword}
              fullWidth
              menuPosition="fixed"
              onChange={(value) => setValue('removeKeywordBrands', value)}
              label={t('brand', { count: 2 })}
              value={values.removeKeywordBrands}
              error={Boolean(errors.removeKeywordBrands)}
              helperText={errors.removeKeywordBrands}
              placeholder={t('__action__relevant__type__', {
                action: t('select'),
                type: t('brand', { count: 2 }),
              })}
            />
          </Grid>
          <Grid item xs={12}>
            <Select
              options={removeTagOptions}
              isMulti
              disabled={values.removeAllKeyword}
              fullWidth
              menuPosition="fixed"
              onChange={(value) => setValue('removeKeywordTags', value)}
              label={t('tag', { count: 2 })}
              value={values.removeKeywordTags}
              error={Boolean(errors.removeKeywordTags)}
              helperText={errors.removeKeywordTags}
              placeholder={t('__action__relevant__type__', {
                action: t('select'),
                type: t('tag', { count: 2 }),
              })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container spacing={2} justifyContent="flex-end">
          <Grid item>
            <Button variant="text" onClick={onCancel}>
              {t('cancel')}
            </Button>
          </Grid>
          <Grid item>
            <ButtonSubmit
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              isSubmitting={isSubmitting}
              label={t('submit')}
              icon={<IconsSend />}
            />
          </Grid>
        </Grid>
      </DialogActions>
    </>
  );
}
